export default function JensenBeachDestinationsSection() {
  const stats = [
    {
      value: "62%",
      label: "of Jensen Beach requests are fishing-related or Bahamas boating travel with under 3 weeks notice",
    },
    {
      value: "2–3 weeks",
      label: "average notice for international fishing tournament registration and Bahamas trips",
    },
    {
      value: "Nov–Apr",
      label: "peak season — passport renewals spike before snowbird international travel",
    },
    {
      value: "48-hour",
      label: "shortest tournament departure window we've accommodated for a Jensen Beach charter captain",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas",
      context: "The most common international destination for Jensen Beach travelers — fishing tournaments, recreational boating runs to West End and Bimini, and family resort vacations to Nassau, Paradise Island, and the Exumas. The St. Lucie Inlet provides direct Atlantic access for boaters, and PBI offers connecting flights through Nassau. Bahamas trips are often planned around weather windows and tournament schedules with 1–3 weeks of notice.",
      urgency: "Weather windows for boat crossings are unpredictable — passport issues discovered during a favorable forecast window are catastrophic for planned trips",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Costa Rica",
      context: "Costa Rica is a premier destination for Jensen Beach anglers seeking Pacific billfish — sailfish, marlin, and dorado — in destinations like Los Sueños, Quepos, and Guanacaste. Tournament circuits including the Offshore World Championship and numerous local Costa Rican tournaments draw Jensen Beach charter captains and competitive anglers. These trips are often booked 2–4 weeks before tournament dates.",
      urgency: "Tournament registration deadlines and charter boat availability create compressed booking windows — insufficient time for standard passport processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Mexico draws Jensen Beach travelers for two distinct reasons: Baja California sportfishing (Cabo San Lucas, La Paz) for billfish and dorado, and Cancun/Riviera Maya resort vacations for families. The Bisbee's Black & Blue Marlin Tournament in Cabo is a major draw for competitive anglers. Spring break bookings to Cancun surge in January and February.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March for Jensen Beach families planning Mexico trips",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Caribbean Cruises",
      context: "Jensen Beach families and retirees regularly cruise to the Bahamas, Jamaica, the Cayman Islands, St. Thomas, and the Eastern Caribbean from Port Everglades and Port Miami. Disney Cruise Line, Royal Caribbean, Carnival, and Norwegian all operate itineraries that appeal to Jensen Beach's family-oriented and retiree demographics. Cruise travel is especially popular during school breaks and the winter snowbird season.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Canada is a popular destination for Jensen Beach snowbirds during the summer months, when they return north to escape Florida's heat and humidity. Ontario's lake regions, Quebec's cultural cities, and the Maritime provinces draw seasonal residents who split their year between Jensen Beach and Canada. Additionally, fishing enthusiasts travel to Ontario and Quebec for freshwater fishing trips.",
      urgency: "Snowbird travel to Canada is often planned in late spring — passport issues discovered during seasonal transition planning can derail summer travel",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & Ireland",
      context: "The UK and Ireland draw Jensen Beach travelers for heritage tourism, golf trips, and cultural exploration. Ireland's golf courses — particularly the links courses of the southwest — attract retired golfers from Jensen Beach's active adult communities. London, Edinburgh, and Dublin offer cultural and historical attractions that appeal to the educated retiree demographic common in Martin County.",
      urgency: "Summer golf and heritage trips are often booked 3–6 weeks in advance — too short for standard government processing",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Jensen Beach Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Jensen Beach
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Jensen Beach travelers head to the Bahamas for fishing, Costa Rica for billfish tournaments, the Caribbean for family cruises, and Europe for golf and heritage trips. These are the destinations that create the most passport urgency for our Jensen Beach clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout the Treasure Coast</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Stuart", href: "/cities/stuart" },
              { name: "Port St. Lucie", href: "/cities/port-st-lucie" },
              { name: "Palm City", href: "/cities/stuart" },
              { name: "Vero Beach", href: "/cities/stuart" },
              { name: "Jupiter", href: "/cities/jupiter" },
              { name: "West Palm Beach", href: "/cities/west-palm-beach" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}